import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, CheckCircle, Phone, Shield, Lock, Users, Building2, AlertTriangle } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, AnimatedCard, CardHeader, CardTitle, CardDescription } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { getServiceById } from '@/data/services'
import { companyInfo } from '@/data/company'

export function Cybersecurity() {
  const service = getServiceById('cybersecurity')!

  return (
    <>
      <SEO
        title="Cybersecurity Solutions"
        description="Protect your organization with comprehensive security solutions designed for modern threats."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="glow" className="mb-6">Cybersecurity</Badge>
              
              <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
                {service.title}
              </h1>

              <p className="text-lg text-slate-300 mb-8">
                {service.longDescription}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="glow" className="group">
                    Get Security Assessment
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phoneRaw}`}>
                  <Button size="lg" variant="secondary">
                    <Phone className="w-5 h-5" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="glass-card p-8 rounded-3xl">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Shield, label: 'Endpoint', desc: 'Device protection' },
                    { icon: Lock, label: 'Email', desc: 'Threat blocking' },
                    { icon: Users, label: 'Training', desc: 'Security awareness' },
                    { icon: Building2, label: 'Compliance', desc: 'Regulatory support' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                      className="glass-card-hover p-6"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-electric-500 flex items-center justify-center mb-4 shadow-glow">
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-white mb-1">{item.label}</h3>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Threat Stats */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge variant="warning" className="mb-4">
              <AlertTriangle className="w-4 h-4" />
              The Threat Landscape
            </Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Financial Institutions Are Prime Targets
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Credit unions and banks face increasingly sophisticated cyber attacks. Our solutions are designed to protect your organization.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { value: '300%', label: 'Increase', desc: 'In cyber attacks on financial institutions' },
              { value: '$5.9M', label: 'Average Cost', desc: 'Of a data breach in financial services' },
              { value: '95%', label: 'Of Breaches', desc: 'Caused by human error' },
              { value: '24/7', label: 'Monitoring', desc: 'Required for effective protection' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card p-6 text-center"
              >
                <div className="text-3xl font-heading font-bold text-accent-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm font-semibold text-white mb-1">{stat.label}</div>
                <div className="text-xs text-slate-400">{stat.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Solutions</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Comprehensive Security Coverage
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {service.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500/20 to-electric-500/20 flex items-center justify-center mb-4">
                      <feature.icon className="w-7 h-7 text-accent-400" />
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4">Benefits</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Why Choose Our Security Solutions?
              </h2>

              <div className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-8 h-8 rounded-full bg-success-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-success-500" />
                    </div>
                    <span className="text-slate-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-xl font-heading font-semibold text-white mb-6">
                Compliance Support
              </h3>
              <p className="text-slate-400 mb-6">
                Our security solutions help you meet regulatory requirements:
              </p>
              <div className="grid grid-cols-2 gap-4">
                {['NCUA', 'FFIEC', 'GLBA', 'PCI DSS', 'SOC 2', 'GDPR'].map((reg) => (
                  <div key={reg} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-accent-400" />
                    {reg}
                  </div>
                ))}
              </div>
              <Link to="/contact" className="block mt-6">
                <Button className="w-full" variant="glow">
                  Request Security Assessment
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}


